import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError, brl } from "@/lib/api";
import { toast } from "sonner";
import { UserCog, ExternalLink } from "lucide-react";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { ProgressBar } from "@/components/panel/ui";
import { Badge, DataTable, Input, PageHeader, SearchInput, Select } from "@/components/ui-kit";
import { usePanelLink } from "@/components/panel/screens";

/**
 * Metas, comissão e desempenho.
 *
 * Criar usuário, trocar senha e definir perfil de acesso vivem em Usuários: são
 * assuntos de acesso, e esta tela é liberada para quem acompanha desempenho.
 */
export default function Sellers() {
  // Link para o painel ATUAL: a mesma tela serve admin, vendedor e balcão.
  const link = usePanelLink();
  const [situacao, setSituacao] = useState("ativos");

  const {
    items: sellers, total, page, lastPage, setPage, search, setSearch,
    isLoading, isError, refetch: load,
  } = usePaginatedList("/admin/sellers", { situacao });

  const updateField = async (s, field, value) => {
    try {
      await api.patch(`/admin/sellers/${s.id}`, { [field]: parseFloat(value) });
      toast.success(field === "meta_mensal" ? `Meta de ${s.name} atualizada` : `Comissão de ${s.name} atualizada`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-sellers-page">
      <PageHeader
        eyebrow="Relacionamento"
        title="Vendedores & Balcão"
        icon={UserCog}
        description="Metas, comissão e desempenho do mês. Criar usuário e definir perfil de acesso ficam em Usuários."
        actions={(
          <>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome ou e-mail" data-testid="sellers-search" aria-label="Buscar vendedor por nome ou e-mail" width="w-52" />
            <Select value={situacao} onChange={(e) => setSituacao(e.target.value)} data-testid="sellers-situacao-filter"
              aria-label="Filtrar por situação" className="w-auto px-2.5 text-slate-600">
              <option value="ativos">Ativos</option>
              <option value="inativos">Desativados</option>
              <option value="todos">Todos</option>
            </Select>
            <Link to={link("usuarios")} data-testid="sellers-to-users"
              className="inline-flex items-center gap-1.5 h-10 px-2 text-sm font-semibold text-brand-600 hover:underline">
              Gerenciar acessos <ExternalLink size={14} aria-hidden />
            </Link>
          </>
        )}
      />

      <DataTable
        caption="Vendedores com metas e comissão do mês"
        rows={sellers}
        rowKey={(s) => s.id}
        loading={isLoading}
        error={isError}
        onRetry={load}
        rowClassName={(s) => (s.active ? undefined : "bg-slate-50/60")}
        empty={{
          icon: UserCog,
          title: "Nenhum vendedor encontrado",
          desc: situacao === "inativos"
            ? "Ninguém foi desativado até agora."
            : "Cadastre vendedores em Usuários para acompanhar metas e comissões aqui.",
          testId: "sellers-empty",
        }}
        columns={[
          {
            key: "vendedor", header: "Vendedor",
            cell: (s) => (
              <>
                <p className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                  {s.name}
                  <Badge tone={s.role === "balcao" ? "purple" : "info"}>{s.role === "balcao" ? "Balcão" : "Vendedor"}</Badge>
                  {!s.active && <Badge>Desativado</Badge>}
                </p>
                <p className="text-xs text-slate-400">{s.email}</p>
              </>
            ),
          },
          {
            key: "rate", header: "Comissão %", align: "right",
            cell: (s) => (
              <Input type="number" step="0.5" defaultValue={s.commission_rate}
                onBlur={(e) => updateField(s, "commission_rate", e.target.value)}
                disabled={!s.active} size="sm" mono data-testid="seller-rate-input"
                aria-label={`Comissão de ${s.name} em porcentagem`}
                className="w-20 px-2 text-right disabled:bg-slate-100" />
            ),
          },
          {
            key: "meta", header: "Meta mensal", align: "right",
            cell: (s) => (
              <Input type="number" step="500" defaultValue={s.meta_mensal}
                onBlur={(e) => updateField(s, "meta_mensal", e.target.value)}
                disabled={!s.active} size="sm" mono data-testid="seller-meta-input"
                aria-label={`Meta mensal de ${s.name}`}
                className="w-28 px-2 text-right disabled:bg-slate-100" />
            ),
          },
          {
            /* Teto de desconto: é regra de venda e vivia só no cadastro de
               Perfis, a duas telas de distância de quem acompanha a equipe.
               Aqui ele fica ao lado da comissão, que é a outra alavanca que o
               dono usa por pessoa. Só leitura: quem o define é o PERFIL de
               acesso — editá-lo por vendedor faria dois vendedores do mesmo
               perfil terem tetos diferentes sem que o perfil dissesse isso. */
            key: "desconto", header: "Desconto máx.", align: "right",
            cell: (s) => (
              <span title={s.profile_name ? `Perfil: ${s.profile_name}` : "Sem perfil de acesso"}>
                {s.max_discount_percent == null
                  ? <span className="text-slate-400 text-xs">sem limite</span>
                  : <span className="font-mono font-medium text-slate-700">{s.max_discount_percent}%</span>}
              </span>
            ),
          },
          { key: "vendido", header: "Vendido (mês)", align: "right", mono: true, cell: (s) => brl(s.month_sold) },
          {
            key: "atingimento", header: "Atingimento",
            cell: (s) => <ProgressBar pct={s.meta_pct} testId="seller-meta-progress" />,
          },
          {
            key: "comissao", header: "Comissão (mês)", align: "right", mono: true,
            className: "font-semibold text-brand-700", cell: (s) => brl(s.month_commission),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="vendedores" />
    </div>
  );
}
